import React, { useState, useEffect } from 'react';
import { Cookie, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { motion, AnimatePresence } from 'motion/react';

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const hasConsented = localStorage.getItem('cookie_consent');
    if (!hasConsented) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (status: 'all' | 'essential' | 'custom') => {
    localStorage.setItem('cookie_consent', status);
    if (status === 'all') {
      localStorage.setItem('cookie_preferences', JSON.stringify({ essential: true, analytics: true, marketing: true }));
    } else if (status === 'essential') {
      localStorage.setItem('cookie_preferences', JSON.stringify({ essential: true, analytics: false, marketing: false }));
    } else {
      localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
    }
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-x-0 bottom-0 z-[100] p-4 flex justify-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="w-full sm:max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col max-h-[85vh]"
          >
            <div className="p-6 overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-emerald-100 dark:bg-emerald-900/40 p-2.5 rounded-xl shrink-0">
                  <Cookie className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white text-lg tracking-tight">Privacy & Cookies</h3>
              </div>
              
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
              </p>

              {showPreferences && (
                <div className="mb-6 overflow-hidden">
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/50 space-y-4">
                    {/* Essential */}
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">Strictly Necessary</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Required for the website to function securely and cannot be switched off.</p>
                      </div>
                      <div className="shrink-0 flex items-center justify-center w-8 h-5 bg-emerald-500 rounded-full relative cursor-not-allowed opacity-80">
                         <div className="w-4 h-4 rounded-full bg-white absolute right-0.5" />
                      </div>
                    </div>
                    {/* Analytics */}
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">Analytics</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Help us understand how visitors interact with the app.</p>
                      </div>
                      <div 
                        onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                        className={`shrink-0 flex items-center w-8 h-5 rounded-full relative cursor-pointer outline-none transition-colors ${preferences.analytics ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                      >
                         <div className={`w-4 h-4 rounded-full bg-white absolute transition-all ${preferences.analytics ? 'left-[14px]' : 'left-[2px]'}`} />
                      </div>
                    </div>
                    {/* Marketing */}
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">Marketing</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Used to deliver advertisements more relevant to you.</p>
                      </div>
                      <div 
                        onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                        className={`shrink-0 flex items-center w-8 h-5 rounded-full relative cursor-pointer outline-none transition-colors ${preferences.marketing ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                      >
                         <div className={`w-4 h-4 rounded-full bg-white absolute transition-all ${preferences.marketing ? 'left-[14px]' : 'left-[2px]'}`} />
                      </div>
                    </div>

                    <div className="pt-2 text-xs text-slate-500 mt-2 border-t border-slate-200 dark:border-slate-700/50">
                      Learn more in our <Link to="/privacy" className="text-emerald-600 dark:text-emerald-400 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-emerald-600 dark:text-emerald-400 hover:underline">Terms of Service</Link>.
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2 mt-auto">
                 {showPreferences ? (
                    <Button
                      variant="primary"
                      onClick={() => saveConsent('custom')}
                      className="w-full"
                    >
                      Save Preferences
                    </Button>
                 ) : (
                    <>
                      <Button
                        variant="primary"
                        onClick={() => saveConsent('all')}
                        className="w-full"
                      >
                        Accept All Cookies
                      </Button>
                      <div className="flex gap-2">
                         <Button
                          variant="outline"
                          onClick={() => saveConsent('essential')}
                          className="flex-1"
                        >
                          Reject Non-Essential
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowPreferences(true)}
                          className="flex-1"
                        >
                          Manage Options
                        </Button>
                      </div>
                    </>
                 )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
